import type {
  Sensor,
  SensorDataPerSubstation,
  SensorReadingValues,
  SensorTypeTechnicalName,
} from "@core/apis/shared";

// --- Heating System Diagram Types ---

/** Configuration for a sensor overlay on the heating diagram */
export type DiagramSensorConfig = {
  type: SensorTypeTechnicalName;
  readingKey: keyof SensorReadingValues;
  label: string;
  x: number;
  y: number;
};

/** A sensor overlay with its latest reading for the diagram */
export type DiagramSensorOverlay = {
  key: string;
  sensor: Sensor;
  type: SensorTypeTechnicalName;
  label: string;
  x: number;
  y: number;
  reading: number | null | undefined;
  timestamp: string | undefined;
};

/** Indoor temperature summary for the diagram */
export type IndoorTemperatureSummary = {
  sensorCount: number;
  averageTemperature: number | null;
};

/** Normalized data for the HeatingSystemDiagram component */
export type HeatingDiagramData = {
  overlays: DiagramSensorOverlay[];
  indoorTemperature: IndoorTemperatureSummary;
};

// --- Normalization Functions ---

/**
 * Parse sensor data into diagram overlays by matching sensors to config positions.
 */
export function buildDiagramOverlays(
  sensors: Sensor[],
  sensorData: Record<string, SensorDataPerSubstation[]>,
  configs: Record<string, DiagramSensorConfig>
): DiagramSensorOverlay[] {
  // Group sensors by type
  const sensorsByType = new Map<string, Sensor[]>();
  for (const sensor of sensors) {
    const type = sensor.sensor_type_technical_name;
    if (!sensorsByType.has(type)) {
      sensorsByType.set(type, []);
    }
    sensorsByType.get(type)!.push(sensor);
  }

  const overlays: DiagramSensorOverlay[] = [];

  for (const [configKey, config] of Object.entries(configs)) {
    const typeSensors = sensorsByType.get(config.type);
    if (!typeSensors || typeSensors.length === 0) continue;

    const sensor = typeSensors[0];
    const readings = sensorData[sensor.uid];
    const latestReading = readings?.slice(-1)[0];

    overlays.push({
      key: configKey,
      sensor,
      type: config.type,
      label: config.label,
      x: config.x,
      y: config.y,
      reading: latestReading?.values?.[config.readingKey] ?? null,
      timestamp: latestReading?.timestamp,
    });
  }

  return overlays;
}

/**
 * Extract indoor temperature summary from average sensor data.
 */
export function buildIndoorTemperatureSummary(
  indoorSensors: Sensor[] | undefined,
  averageData: SensorDataPerSubstation[] | undefined
): IndoorTemperatureSummary {
  const latestAvg = averageData?.slice(-1)[0];
  return {
    sensorCount: indoorSensors?.length ?? 0,
    averageTemperature: latestAvg?.values?.living_air_temperature ?? null,
  };
}
